import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PhoneInput } from "react-international-phone";
import {
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import CountrySelect from "../utils/countrySelect";
import ErrorModal from "../utils/ErrorModal";
import ButtonBack from "../utils/ButtonBack";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots
const SEC_COLORS = {
  company: "#2F7FD1",
  contact: "#12A08F",
};

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c) => ({ width: 6, height: 6, flex: "0 0 6px", borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const labelStyle = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const inputStyle = (hasError, mono) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: mono ? MONO : FONT,
  fontSize: mono ? 13 : 13.5,
  color: INK,
  outline: "none",
});
const errorStyle = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const primaryBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules (form, border-container,
// phone-container, inputs, labels) can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-org .border-container,.dc-org form.form,.dc-org .dashboard-container,.dc-org .add-forms{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important;background:transparent!important;border:0!important;box-sizing:border-box!important;}
.dc-org .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-org .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-org .dc-card input{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;height:36px!important;font-size:13.5px!important;}
.dc-org .dc-card .phone-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;}
.dc-org .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-org .dc-card input::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-org .dc-card p.error{margin:4px 0 0!important;}
.dc-org .dc-head-btns{display:flex!important;flex-direction:row!important;margin:0!important;padding:0!important;align-items:center!important;align-self:flex-end!important;}
.dc-org .dc-head-btns .MuiButton-root,.dc-org .dc-head-btns button{margin:0!important;height:38px!important;min-height:38px!important;max-height:38px!important;padding-top:0!important;padding-bottom:0!important;line-height:1!important;position:static!important;inset:auto!important;top:auto!important;bottom:auto!important;transform:none!important;translate:none!important;align-self:center!important;margin-block:0!important;vertical-align:middle!important;box-sizing:border-box!important;}
`;

const AddPartner = () => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const { partnerId } = useParams();
  const [form, setForm] = useState({
    companyName: "",
    partnerName: "",
    email: "",
    mobile: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [country, setCountry] = useState("IN");
  const orgId = JSON.parse(localStorage.getItem("org"));
  const isMobile = useMediaQuery("(max-width: 750px)");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const [goToPartners, setGoToPartners] = useState(false);

  const showModal = (msg, type, navigateAfter = false) => {
    setModalMsg(msg);
    setModalType(type);
    setGoToPartners(navigateAfter);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    if (goToPartners) navigate("/dashboard/partners");
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const REQUIRED_FIELDS = ["companyName", "partnerName", "email", "mobile"];

  const handleSubmit = async () => {
    const newErrors = {};

    REQUIRED_FIELDS.forEach((field) => {
      if (!form[field] || form[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
      const isValidMobile = (num) => /^\d{7,12}$/.test(num);

      if (field === "mobile" && !isValidMobile(form.mobile)) {
        newErrors.mobile = "Invalid mobile number";
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/partner`,
        {
          orgId: orgId,
          companyName: form.companyName,
          partnerName: form.partnerName,
          email: form.email,
          mobile: form.mobile.replace(/\D/g, ""),
          countryCode: getCountryCallingCode(country),
        },
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      showModal("Partner added successfully", "success", true);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.Exception || "Failed to add partner");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    const newErrors = {};

    REQUIRED_FIELDS.forEach((field) => {
      if (!form[field] || form[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/partner?partnerId=${partnerId}`,
        {
          ...form,
          orgId: orgId,
          mobile: form.mobile.replace(/\D/g, ""),
          countryCode: getCountryCallingCode(country),
        },
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      showModal("Partner updated successfully", "success", true);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.Exception || "Failed to update partner");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (partnerId !== undefined) {
      const getPartner = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/partner?partnerId=${partnerId}&orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          let parsedPhone = null;

          if (res.data.mobile) {
            parsedPhone = parsePhoneNumber(
              res.data.countryCode != null
                ? res.data.countryCode.includes("+")
                  ? `${res.data.countryCode}${res.data.mobile}`
                  : `+${res.data.countryCode}${res.data.mobile}`
                : `+${res.data.mobile}`,
            );
            if (parsedPhone?.country) {
              setCountry(parsedPhone.country);
            }
          }
          setForm(res.data);
        } catch (error) {
          console.log(error);
        }
      };

      getPartner();
    }
  }, [orgId]);

  const handleBack = () => {
    navigate(`/dashboard/inventory`);
  };

  // Cancel / back goes to the partners list (same as the old Back button)
  const goPartners = () => navigate("/dashboard/partners");

  /* ---------- markup helpers (same handlers as before) ---------- */
  const fieldBox = (name, label, control, err) => (
    <Box key={name}>
      <label style={labelStyle}>
        {label}
        <span style={req}>*</span>
      </label>
      {control}
      {err && (
        <span className="error-text" style={errorStyle}>
          {err}
        </span>
      )}
    </Box>
  );

  const section = (id, title, meta, children) => (
    <section key={id} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[id])} />
          {title}
        </span>
        <span style={cardMeta}>{meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(3)}>{children}</Box>
      </Box>
    </section>
  );

  const saveButton = partnerId ? (
    <Button className="btn-contained" onClick={handleUpdate} disabled={loading} sx={primaryBtn}>
      {loading ? "Updating..." : "Update Partner"}
    </Button>
  ) : (
    <Button className="btn-contained" onClick={handleSubmit} disabled={loading} sx={primaryBtn}>
      {loading ? "Adding..." : "Add Partner"}
    </Button>
  );

  return (
    <>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-org"
        style={{ fontFamily: FONT, color: INK, textAlign: "left", padding: isMobile ? "4px 0 88px" : "4px 0 32px" }}
      >
        {/* ---------------- PAGE HEADER ---------------- */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
          <div style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={goPartners}
              style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              ← Partners
            </button>
            <Typography
              component="h1"
              sx={{ m: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}
            >
              {partnerId ? "Edit partner" : "Add partner"}
            </Typography>
            <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {partnerId
                ? [form.partnerName, form.companyName].filter(Boolean).join(" · ") || "Partner details"
                : "Add a channel partner who can bring in leads"}
            </div>
          </div>
          {!isMobile && (
            <Box className="dc-head-btns" sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: "0 0 auto" }}>
              <Button className="btn-outlined" onClick={goPartners} sx={ghostBtn}>
                Cancel
              </Button>
              {saveButton}
            </Box>
          )}
        </Box>

        {error && (
          <div
            style={{
              color: "#A6342A",
              marginBottom: 14,
              padding: "10px 14px",
              background: "#FBEDEB",
              border: "1px solid #F2CFCA",
              borderRadius: 12,
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        )}

        <div className="add-forms" style={{ width: "100%", margin: 0, padding: 0, background: "transparent", border: 0, boxShadow: "none" }}>
          {/* ---------------- COMPANY ---------------- */}
          {section("company", "Company", "1 required", [
            fieldBox(
              "companyName",
              "Company Name",
              <input
                className={`${errors.companyName && "input-error"}`}
                type="text"
                name="companyName"
                placeholder="Company name"
                value={form.companyName}
                onChange={handleChange}
                style={inputStyle(!!errors.companyName)}
              />,
              errors.companyName,
            ),
          ])}

          {/* ---------------- CONTACT ---------------- */}
          {section("contact", "Contact", "3 required", [
            fieldBox(
              "partnerName",
              "Partner Name",
              <input
                className={`${errors.partnerName && "input-error"}`}
                type="text"
                name="partnerName"
                placeholder="Full name"
                value={form.partnerName}
                onChange={handleChange}
                required
                style={inputStyle(!!errors.partnerName)}
              />,
              errors.partnerName,
            ),
            fieldBox(
              "email",
              "Email",
              <input
                className={`${errors.email && "input-error"}`}
                type="email"
                name="email"
                placeholder="name@example.com"
                value={form.email}
                onChange={handleChange}
                style={inputStyle(!!errors.email)}
              />,
              errors.email,
            ),
            fieldBox(
              "mobile",
              "Mobile",
              <div
                className={`phone-container ${errors.mobile ? "input-error" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 36,
                  boxSizing: "border-box",
                  background: FIELD_BG,
                  border: `1px solid ${errors.mobile ? ERROR : FIELD_EDGE}`,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <div
                  className="dc-cc"
                  style={{
                    flex: "0 0 auto",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    padding: "0 6px 0 10px",
                    borderRight: `1px solid ${FIELD_EDGE}`,
                    boxSizing: "border-box",
                  }}
                >
                  <CountrySelect value={country} onChange={setCountry} />
                </div>

                <input
                  type="tel"
                  name="mobile"
                  className="dc-bare"
                  placeholder="Enter phone number"
                  value={form.mobile}
                  onChange={handleChange}
                  style={{ flex: 1, minWidth: 0, height: "100%", margin: 0, padding: "0 12px 0 10px", border: 0, outline: "none", background: "transparent", fontFamily: FONT, fontSize: 13.5, color: INK }}
                />
              </div>,
              errors.mobile,
            ),
          ])}
        </div>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
              p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={goPartners} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={handleModalClose}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </>
  );
};

export default AddPartner;